<template>
  <b-form-group label-class="product-creation-labels" class="mb-0">
    <template slot="label">
      Category <span class="font-weight-bold text-danger">*</span>
    </template>
    <b-dropdown
        id="category-dropdown"
        dropright
        :text="categoryDisplayText"
        variant="ghost"
        class="w-100"
        :toggle-class="['text-left border overflow-hidden pr-3', {
          'text-no-selected': !selectedCategory
        }]"
        ref="categoryDropdown"
        menu-class="m-0 p-0"
        :disabled="isDisabled">
        <b-dropdown-header class="px-0" ref="dropdownHeader">
          <div class="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
            Select Category
            <img
              src="@/assets/images/icons/close.png"
              class="close-category"
              alt="Close"
              @click="closeDropdown"
            >
          </div>
          <b-form-group class="py-2 px-3 border-bottom mb-0">
            <b-form-input
              class="search-input rounded-0 border-0"
              v-model="searchKeyword"
              placeholder="Search"
              @keyup.native="debounceSearch">
            </b-form-input>
          </b-form-group>
        </b-dropdown-header>
        <div class="scroll-content py-2 px-3">
          <template v-if="loading">
            <div class="text-center text-muted py-3 px-2">Loading...</div>
          </template>
          <template v-else>
            <div
              :class="['category-item rounded border mb-2 py-2 px-3', {
                'border-selected': selectedCategory
                  && Number(selectedCategory) === Number(prodCat.id)
              }]"
              v-for="(prodCat, prodCatIndex) in transformedCategories"
              :key="`product-category-${prodCat.id}`"
              @click="setSelectedCategory(prodCat)"
              :id="`${origin}-category-${prodCat.id}`"
              :ref="
                prodCatIndex === transformedCategories.length - 1
                  && !prodCat.id
                ? 'activeNewCategory'
                : prodCatIndex === transformedCategories.length - 1
                  && prodCat.id
                  ? 'lastCategory'
                  : null
              ">

              <template v-if="!prodCat.id && prodCatIndex === transformedCategories.length - 1">
                <b-form-group class="m-0 p-0">
                  <div class="d-flex justify-content-between">
                    <b-form-input
                      v-model="newCategory"
                      class="new-category rounded-0 border-0 py-0 pl-0"
                      ref="newCategoryField"
                      @keyup.enter.native="newCategory ? submitNewCategory() : null">
                    </b-form-input>
                    <b-button
                      class="submit-new-category bg-transparent rounded-0 border-0 p-0 shadow-none"
                      @click="submitNewCategory"
                      :disabled="!newCategory || isSaving">
                      Add
                    </b-button>
                  </div>
                </b-form-group>
              </template>
              <template v-else>
                <b-form-group class="m-0 p-0">
                  <div class="d-flex justify-content-between">
                    <b-form-input
                      v-model="prodCat.name"
                      class="bg-transparent rounded-0 border-0 p-0 flex-grow-1"
                      :disabled="!enabledEditCategoryId
                        || Number(enabledEditCategoryId) !== Number(prodCat.id)"
                      @keyup.enter.native="submitUpdateCategory(prodCat)"
                      :ref="`editCategory-${prodCat.id}`">
                    </b-form-input>
                    <template v-if="enabledEditCategoryId
                      && Number(enabledEditCategoryId) === Number(prodCat.id)">
                      <b-button
                        class="cancel-edit-category bg-transparent rounded-0 border-0 p-0 shadow-none mx-2"
                        :disabled="isSaving"
                        @click="cancelEditCategory(prodCat)">
                        Cancel
                      </b-button>
                      <b-button
                        class="submit-new-category bg-transparent rounded-0 border-0 p-0 shadow-none"
                        @click="submitUpdateCategory(prodCat)"
                        :disabled="isSaving || !prodCat.name">
                        Save
                      </b-button>
                    </template>
                    <div
                      class="btn-action-container"
                      v-if="!enabledEditCategoryId
                        && Number(enabledEditCategoryId) !== Number(prodCat.id)">
                      <b-button
                        class="bg-transparent p-0 border-0 rounded-0 shadow-none mr-2"
                        @click="enableCategoryField(prodCat)">
                        <img src="~@/assets/images/u_edit-alt.svg" alt="Edit">
                      </b-button>
                      <b-button
                        class="bg-transparent p-0 border-0 rounded-0 shadow-none"
                        @click="deleteCategory(prodCat)">
                        <img src="~@/assets/images/u_trash-alt.svg" alt="Delete">
                      </b-button>
                    </div>
                  </div>
                </b-form-group>
              </template>
            </div>
          </template>
          <b-form-invalid-feedback :force-show="errors && errors['category_name'] ? true : false">
            {{ errors && errors['category_name'] ? errors['category_name'] : null }}
          </b-form-invalid-feedback>
        </div>
        <b-button
          block
          variant="ghost"
          class="add-category border-top text-center p-2"
          @click="addNewCategory"
          :disabled="isAddCategoryDisabled">
          + Add Category
        </b-button>
    </b-dropdown>
  </b-form-group>
</template>

<script>
import _ from "lodash";
import { mapGetters, mapActions, mapMutations } from "vuex";
import notification from '@/services/notification';

export default {
  props: ["selectedCategory", "isDisabled", "origin"],
  data() {
    return {
      searchKeyword: null,
      newCategory: null,
      enabledEditCategoryId: null,
      originalCategoryName: null,
      transformedCategories: [],
      selectedCategoryDisplay: {}
    }
  },
  computed: {
    ...mapGetters({
      categories: "estoreInventories/categories",
      filteredCategories: "estoreInventories/filteredCategories",
      loading: "estoreInventories/loading",
      isSaving: "estoreInventories/isSaving",
      errors: "estoreInventories/errors"
    }),
    categoryDisplayText: {
      get() {
        const category = this.categories.find(category => Number(category.id) === Number(this.selectedCategory));
        const transformedCategory = this.transformedCategories.find(category => Number(category.id) === Number(this.selectedCategory));

        this.selectedCategoryDisplay = category ? category : transformedCategory;
        return _.isEmpty(this.selectedCategoryDisplay) || !this.selectedCategory
          ? "Select Category"
          : this.selectedCategoryDisplay.name;
      },
      set(name) {
        return name;
      }
    },
    isAddCategoryDisabled: {
      get() {
        const lastCategory = JSON.parse(JSON.stringify(this.transformedCategories));
        return (this.transformedCategories.length > 0
          && !lastCategory.pop().id)
          || !!this.enabledEditCategoryId;
      },
      set(status) {
        return status;
      }
    }
  },
  mounted() {
    this.$root.$on('bv::dropdown::show', async (bvEvent) => {
      this.transformedCategories = this.categories;
      this.enabledEditCategoryId = null;
      this.searchKeyword = null;
      this.setErrors(null);
    });
    this.$root.$on('bv::dropdown::hide', async (bvEvent) => {
      if (this.enabledEditCategoryId) {
        const category = this.categories.find(cat => Number(cat.id) === Number(this.enabledEditCategoryId));
        this.cancelEditCategory(category);
      }
    });
  },
  created() {
    this.debounceSearch = _.debounce(() => {
      this.getFilteredCategories({ category_name: this.searchKeyword });
    }, 300);
  },
  methods: {
    ...mapActions({
      getCategories: "estoreInventories/getCategories",
      getFilteredCategories: "estoreInventories/getFilteredCategories",
      addCategory: "estoreInventories/addCategory"
    }),
    ...mapMutations({
      setErrors: "estoreInventories/errors"
    }),
    closeDropdown() {
      this.$refs.categoryDropdown.hide();
    },
    setSelectedCategory(category) {
      if (this.enabledEditCategoryId
        && this.enabledEditCategoryId !== category.id) {
          notification.warning("Oops, you might have unsaved changes.");
        } else {
          this.$emit("setSelectedCategory", category.id);
          this.selectedCategoryDisplay = category;
        }
    },
    addNewCategory() {
      this.searchKeyword = null;
      this.newCategory = null;
      this.transformedCategories = this.categories.filter(category => category.id);
      this.transformedCategories.push({ id: null, name: null });

      this.$nextTick(() => {
        const newCategoryEl = this.$refs.activeNewCategory;
        newCategoryEl[0].scrollIntoView({ behavior: 'smooth' });
        const newCategoryField = this.$refs.newCategoryField;
        setTimeout(() => {
          newCategoryField[0].focus();
        }, 300);
      });
    },
    enableCategoryField(category) {
      this.enabledEditCategoryId = category.id;
      this.originalCategoryName = category.name;
      this.$nextTick(() => {
        this.$refs[`editCategory-${category.id}`][0].$el.focus();
      });
    },
    async submitNewCategory() {
      const payload = {
        category_name: this.newCategory
      };
      const allCategoryNames = this.categories
        .filter(category => category.name)
        .map(category => {
          return category.name.toLowerCase();
        });
      if (this.newCategory && allCategoryNames.includes(this.newCategory.toLowerCase())) {
        notification.warning("Oops, something went wrong. Please try again.");
        this.setErrors({
          errors: {
            category_name: 'Category name already exists.'
          }
        });
      } else this.setErrors(null);

      if (!this.isSaving && _.isEmpty(this.errors)) {
        await this.addCategory(payload);
        if (_.isEmpty(this.errors)) {
          this.newCategory = null;
          this.searchKeyword = null;
          await this.getCategories();
          if (!this.loading) {
            const lastCategory = this.$refs.lastCategory;
            lastCategory[0].scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    },
    async submitUpdateCategory(category) {
      this.$store.commit("estoreInventories/errors", null);
      const payload = {
        category_id: category.id,
        category_name: category.name
      };

      if (!this.isSaving && _.isEmpty(this.errors)) {
        await this.$store.dispatch("estoreInventories/updateCategory", payload);
        const categoryErrors = document.getElementsByClassName("error-category");
        Object.values(categoryErrors).map(categoryError => categoryError.remove());
        if (_.isEmpty(this.errors)) {
          this.enabledEditCategoryId = null;
        } else {
          const categoryWithError = document.getElementById(`${this.origin}-category-${this.errors.id}`);
          const parentContainer = categoryWithError.parentElement;
          const categoryError = document.createElement("div");
          categoryError.classList.add("invalid-feedback", "d-block", "mb-2", "error-category");
          categoryError.append(this.errors.error);
          parentContainer.insertBefore(categoryError, categoryWithError.nextElementSibling);
        }
      }
    },
    cancelEditCategory(category) {
      if (this.searchKeyword) {
        this.transformedCategories
          .find(cat => Number(cat.id) === Number(category.id))
          .name = this.originalCategoryName;
      } else {
        this.categories
          .find(cat => Number(cat.id) === Number(category.id))
          .name = this.originalCategoryName;
      }
      this.enabledEditCategoryId = null;
      this.$store.commit("estoreInventories/errors", null);
      const categoryErrors = document.getElementsByClassName("error-category");
      Object.values(categoryErrors).map(categoryError => categoryError.remove());
    },
    async deleteCategory(category) {
      if (!this.isSaving) {
        await this.$store.dispatch("estoreInventories/deleteCategory", category.id);
        if (_.isEmpty(this.errors)) {
          this.$store.dispatch("estoreInventories/getCategories");
          this.searchKeyword = null;
        }
      }
    }
  },
  watch: {
    categories(categories) {
      this.transformedCategories = categories;
    },
    filteredCategories(categories) {
      this.transformedCategories = categories;
    }
  }
}
</script>

<style lang="scss">
#category-dropdown {
  .dropdown-toggle {
    color: rgb(42, 42, 42);
    text-overflow: ellipsis;
    &.text-no-selected {
      color: #87929D;
    }
    &.disabled {
      cursor: not-allowed;
    }
    &::after {
      position: absolute;
      right: 10px;
      top: 12px;
      width: 8px;
      height: 11px;
      background: url("~@/assets/images/caret-right-2.svg") no-repeat center;
      border: 0;
    }
  }
  .dropdown-menu {
    box-shadow: 0px 0px 9px #E7E7E7;
    border-color: #E9EBED;
    border-radius: 8px;
    .scroll-content {
      width: 320px;
      max-height: 300px;
      overflow-y: auto;
    }
    .dropdown-header {
      font-family: MainFontBold, sans-serif;
      font-size: 18px;
      color: #2F3941;
      .close-category {
        width: 12px;
        height: 12px;
        cursor: pointer;
      }
      .search-input {
        background: url("~@/assets/images/search.svg") no-repeat center left 5px;
        padding-left: 2.5rem !important;
        &::placeholder {
          color: #87929D;
        }
      }
    }
    .category-item {
      cursor: pointer;
      border-width: 1.5px !important;
      &.border-selected {
        border-width: 2px !important;
        border-color: #5182FF !important;
      }
      .btn-action-container {
        display: none;
        transition: all ease 0.2s;
      }
      &:hover .btn-action-container {
        display: inherit;
      }
      .form-control {
        pointer-events: none;
        height: inherit !important;
        min-height: 24px;
        &:disabled {
          cursor: pointer;
          background-color: transparent !important;
        }
        &:not(.new-category),
        &:not(:disabled) {
          text-overflow: ellipsis;
        }
      }
    }
    .btn.submit-new-category {
      font-family: MainFontBold, sans-serif;
      color: #5182FF;
    }
    .btn.cancel-edit-category {
      color: #87929D;
    }
    .btn.add-category {
      font-family: MainFontBold, sans-serif;
      color: #5182FF;
      &.disabled {
        color: #87929D;
        cursor: not-allowed;
      }
    }
  }
}
</style>
